import useRequest from "@/lib/request/useRequest";
import {
    Button,
    Card,
    CardBody,
    Chip,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    useDisclosure,
} from "@heroui/react";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { FieldRecommendation } from "@/types/field";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import GenerateRx from "../rx/GenerateRx";
import { GenerateRxResponse } from "@/types/rx";
import { useNavigate } from "@tanstack/react-router";
import { OnDemandRxType } from "@/types/enums";
import useAuth from "@/lib/auth/useAuth";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { faEdit, faRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Resource } from "@/types/user";
import ApplyNChip from "../common/ApplyNChip";
import UserRecommendationForm from "./UserRecommendationForm";
import DoNotApplyNChip from "../common/DoNotApplyNChip";

function RecommendationCard() {
    const navigate = useNavigate();
    const { userIsSentinel, isAtLeastDetail, isConsultantOrSentinel } =
        useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();
    const { fieldId, fieldQuery } = useFieldDashboard() ?? {};
    const { sidedressOnDemandRx, customRecommendationsEnabled } =
        useFeatureFlag();
    const { t } = useTranslation(["common", "messages"]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { formatDateMMDDYYYY } = useI18n();

    const recommendation = useGetRequest<FieldRecommendation>({
        url: `fields/${fieldId}/recommendation/`,
    });

    const userRecommendationContent = useMemo(() => {
        if (
            recommendation.data?.user_recommendation != null &&
            recommendation.data?.user_recommendation.recommend_application !=
            null
        ) {
            return recommendation.data?.user_recommendation
                .recommend_application ? (
                <ApplyNChip />
            ) : (
                <DoNotApplyNChip />
            );
        }
        return null;
    }, [recommendation.data?.user_recommendation]);

    const platformRecommendationContent = useMemo(() => {
        if (recommendation.data?.gap) {
            return (
                <Chip
                    classNames={{
                        base: "bg-gray-100",
                        content: "text-gray-900 font-bold",
                    }}
                    size="sm"
                    radius="sm"
                >
                    {t("image_gap", { ns: "fields-list" })}
                </Chip>
            );
        }

        if (recommendation.data?.recent_application) {
            return (
                <Chip
                    classNames={{
                        base: "bg-green-100 font-bold",
                        content: "font-bold text-green-700",
                    }}
                    radius="sm"
                    size="sm"
                >
                    {t("do_not_apply_n", { ns: "fields-list" })}
                </Chip>
            );
        }

        if (recommendation.data?.recommend_fertigation) {
            return <ApplyNChip />;
        }

        return <DoNotApplyNChip />;
    }, [
        recommendation.data?.gap,
        recommendation.data?.recent_application,
        recommendation.data?.recommend_fertigation,
        t,
    ]);

    const recDate = useMemo(() => {
        if (recommendation.data?.user_recommendation?.recommendation_date) {
            return formatDateMMDDYYYY(
                recommendation.data?.user_recommendation.recommendation_date,
            );
        }

        const d = dayjs(recommendation.data?.recommendation_date);
        if (d.isValid()) {
            return formatDateMMDDYYYY(recommendation.data?.recommendation_date);
        }
        return formatDateMMDDYYYY(dayjs());
    }, [
        recommendation.data?.user_recommendation?.recommendation_date,
        recommendation.data?.recommendation_date,
        formatDateMMDDYYYY,
    ]);

    const generateRxRequest = useMutateRequest<GenerateRxResponse>({
        url: `prescriptions/on_demand/`,
        method: "post",
        errorMessage: t("error.failed_to_generate_rx"),
        successMessage: t("success.generated_rx"),
        onSuccess: (data) => {
            navigate({
                to: `/dashboard/fields/${fieldId}/rx/${data.prescription_id}`,
            });
            onClose();
        },
    });

    const handleMakeRx = useCallback(() => {
        if (userIsSentinel || sidedressOnDemandRx) {
            onOpen();
        } else {
            generateRxRequest.mutate({
                data: {
                    field: Number(fieldId),
                    on_demand_type: OnDemandRxType.ShortTerm,
                    imagery: fieldQuery?.data?.recommendation?.imagery_id,
                    technique: "Fertigation",
                },
            });
        }
    }, [
        fieldId,
        fieldQuery?.data?.recommendation?.imagery_id,
        generateRxRequest,
        onOpen,
        userIsSentinel,
        sidedressOnDemandRx,
    ]);

    const [showEditRecommendation, setShowEditRecommendation] = useState(false);

    if (!fieldQuery?.data?.recommendation?.insights_active) {
        return null;
    }

    return (
        <div>
            <Card className="m-2">
                <CardBody>
                    <div className="flex flex-col gap-1">
                        <div className="flex flex-row justify-between items-center">
                            <div className="col-span-2">
                                <span className="font-bold">Date:</span>{" "}
                                {recDate}
                            </div>
                            {recommendation.data &&
                                customRecommendationsEnabled &&
                                isConsultantOrSentinel && (
                                    <div>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            startContent={
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                />
                                            }
                                            onPress={() =>
                                                setShowEditRecommendation(true)
                                            }
                                        >
                                            Edit
                                        </Button>
                                        {showEditRecommendation && (
                                            <UserRecommendationForm
                                                fieldIds={[fieldId!]}
                                                onClose={(refetch) => {
                                                    setShowEditRecommendation(
                                                        false,
                                                    );
                                                    if (refetch) {
                                                        recommendation.refetch();
                                                    }
                                                }}
                                                platformRecommendationContent={
                                                    platformRecommendationContent
                                                }
                                                recommendation={
                                                    recommendation.data
                                                }
                                            />
                                        )}
                                    </div>
                                )}
                        </div>
                        <div>
                            <span className="font-bold">
                                {t("recommendation", { ns: "insights" })}:{" "}
                            </span>
                            <span>
                                {userRecommendationContent ??
                                    platformRecommendationContent}
                            </span>
                            {recommendation?.data?.user_recommendation
                                ?.recommend_application !== null &&
                                !!recommendation?.data?.user_recommendation
                                    ?.user && (
                                    <span className="text-xs text-gray-500">
                                        {" "}
                                        recommended by{" "}
                                        {
                                            recommendation?.data
                                                ?.user_recommendation.user
                                        }
                                    </span>
                                )}
                        </div>

                        {recommendation.data?.user_recommendation?.notes && (
                            <div>
                                <span className="font-bold">Notes: </span>
                                <span className="text-sm">
                                    {
                                        recommendation.data?.user_recommendation
                                            .notes
                                    }
                                </span>
                                <span className="text-xs text-gray-500">
                                    {" "}
                                    -{" "}
                                    {
                                        recommendation?.data
                                            ?.user_recommendation.user
                                    }
                                </span>
                            </div>
                        )}

                        <div>
                            {/* TODO: remove this feature flag requirement after figuring out optimal rate */}
                            {(userIsSentinel ||
                                sidedressOnDemandRx ||
                                (!recommendation.data?.gap &&
                                    recommendation.data?.insights_active) ||
                                isAtLeastDetail(Resource.GENERATE_RX)) && (
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onPress={handleMakeRx}
                                        startContent={
                                            <FontAwesomeIcon icon={faRotate} />
                                        }
                                        isLoading={generateRxRequest.isPending}
                                    >
                                        {t("generateRx", { ns: "insights" })}
                                    </Button>
                                )}
                        </div>
                        {recommendation.data?.gap && (
                            <div className="text-red-500 text-xs">
                                {t("qualityImageryNotReceivedSince", {
                                    ns: "insights",
                                })}{" "}
                                {formatDateMMDDYYYY(
                                    recommendation.data?.recommendation_date,
                                )}
                            </div>
                        )}
                        {recommendation.data?.recent_application &&
                            !recommendation.data?.gap && (
                                <div className="text-red-500 text-xs">
                                    {t("recentApplicationOn", {
                                        ns: "insights",
                                    })}
                                    {formatDateMMDDYYYY(
                                        recommendation.data?.recent_application,
                                    )}
                                </div>
                            )}
                    </div>
                </CardBody>
            </Card>
            {isOpen && (
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    isDismissable
                    size="xl"
                >
                    <ModalContent>
                        <ModalHeader>
                            {t("generateRx", { ns: "insights" })}
                        </ModalHeader>
                        <ModalBody>
                            <GenerateRx onClose={onClose} />
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
        </div>
    );
}

export default RecommendationCard;
